import { useState, useEffect, useCallback } from 'react';
import { Auth } from 'aws-amplify';
import { useAppContext } from '../libs/contextLib';

const useViewBlocking = () => {
    const [blockedViews, setBlockedViews] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const { isAuthenticated } = useAppContext({ isAuthenticated: false });

    useEffect(() => {
        const loadBlockedViews = async () => {
            try {
                if (!isAuthenticated) {
                    setIsAdmin(false);
                    setBlockedViews([]);
                    setIsLoading(false);
                    return;
                }
                const user = await Auth.currentUserInfo();
                const userIsAdmin = user?.attributes?.['custom:isAdmin'] === 'true';
                setIsAdmin(userIsAdmin);

                // Admins are never blocked
                if (userIsAdmin) {
                    setBlockedViews([]);
                    setIsLoading(false);
                    return;
                }

                const blockedViewsStr = user?.attributes?.['custom:view:blocked'] || '';
                const blockedViewsList = blockedViewsStr
                    .split(',')
                    .map(view => view.trim())
                    .filter(view => view.length > 0);

                setBlockedViews(blockedViewsList);
            } catch (error) {
                console.error('Error loading blocked views:', error);
                setBlockedViews([]);
            } finally {
                setIsLoading(false);
            }
        };

        loadBlockedViews();
    }, [isAuthenticated]);

    const isViewBlocked = useCallback((path) => {
        // Remove leading slash and normalize path
        const normalizedPath = path.replace(/^\//, '');
        
        // Check if any blocked view matches the path
        return blockedViews.some(blockedView => {
            const normalizedBlockedView = blockedView.replace(/^\//, '');
            
            // Exact match or prefix match (for nested routes)
            return normalizedPath === normalizedBlockedView || 
                   normalizedPath.startsWith(normalizedBlockedView + '/');
        });
    }, [blockedViews]);

    const isMenuItemBlocked = useCallback((menuPath) => {
        return !isAdmin && isViewBlocked(menuPath);
    }, [isAdmin, isViewBlocked]);

    return {
        blockedViews,
        isLoading,
        isAdmin,
        isViewBlocked,
        isMenuItemBlocked
    };
};

export default useViewBlocking; 